<template>
  <a-modal :visible="visible" :wrap-class-name="$style.modalSync" :closable="false">
    <p class="mt-2">
      {{ $t('productAvailability.export_excel') }}
    </p>
    <a-spin :spinning="isCalculatingData || isLoading" :tip="$t('common.wait') || 'Mohon tunggu...'">
      <div>
        <label>{{ $t('productAvailability.date_filter_label') }}</label>
        <a-range-picker
          class="date-container w-100"
          :ranges="ranges"
          :format="dateFormat"
          :value="[startDate, endDate]"
          :placeholder="[
            $t('dashboardContent.start_date'),
            $t('dashboardContent.end_date'),
          ]"
          :disabled-date="disabledDate"
          :disabled="isLoading"
          allow-clear
          @change="onChangeDate"
        >
          <DateIcon slot="suffixIcon" style="color: #999999" />
        </a-range-picker>
      </div>
      <div class="mt-3">
        <label>{{ $t('productAvailability.movement_type_label') }}</label>
        <a-select 
          v-model="selectedMovementType" 
          allow-clear
          :max-tag-count="1"
          show-search
          mode="multiple"
          class="w-100 select-antd-default" 
          option-filter-prop="children"
          :filter-option="filterOption"
          :placeholder="$t('productAvailability.movement_type_select')"
          :disabled="isLoading"
          @select="onSelectStateMovement"
          @deselect="onDeselectStateMovement"
          @blur="onBlurMovementType"
        >
          <a-select-option v-for="(item, index) in movementStatus" :key="index" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </div>
    </a-spin>
    <template #footer>
      <div class="d-flex justify-content-between">
        <div>{{ $t('productAvailability.total_data') }}: {{ isCalculatingData ? '...' : totalRowData }}</div>
        <div>
          <a-button key="back" :disabled="isLoading" class="mr-2" @click="typeof back !== 'undefined' && back(), resetData(), onClose()">
            Cancel
          </a-button>
          <a-button key="submit" :disabled="isCalculatingData || totalRowData === 0" type="primary" @click="onSubmitModal()">
            {{ $t('productAvailability.export_excel') }}
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script>
import getQueryParam from '@/utils/getQueryParam'
import DateIcon from '@/components/Icons/DateV2.vue'
import { getDetailStockMovementBySKU } from '@/api/inventory'
import debounce from 'lodash/debounce'

export default {
  name: 'ModalExportExcel',
  components: {
    DateIcon,
  },
  mixins: [getQueryParam],
  props: {
    visible: Boolean,
    back: Function,
    onClose: Function,
    isLoading: Boolean,
    movementStatus: Array,
    catalogId: String,
  },
  setup() {
  },
  data() {
    return {
      dateFormat: 'DD MMMM YYYY',
      startDate: '',
      endDate: '',
      selectedMovementType: ['ALL'],
      totalRowData: 0,
      isCalculatingData: false,
      VISIBLE_MOVEMENT_TYPES: [
        'INBOUND', 'INBOUND_CANCEL', 'OUTBOUND', 'OUTBOUND_RELEASE', 'ADJIN', 'ADJOUT',
        'ADJ_BOOKING', 'BOOKING', 'BOOKING_RELEASE', 'TRANSFERIN', 'TRANSFEROUT',
        'TRANSFEROUT_RELEASE', 'TSTIN', 'TSTOUT', 'PTPIN', 'PTPOUT', 'FGINBOUND',
        'FGCOMPOUTBOUND', 'RETURN', 'RETURN_RELEASE', 'RESERVED',
      ],
      HIDDEN_MOVEMENT_TYPES: [
        'ITEM_ALLOCATION', 'ITEM_ALLOCATION_RELEASE', 'ADJUSTMENT',
        'RECLASSIFICATION_IN', 'RECLASSIFICATION_OUT', 'ONHAND_RELEASE',
        'INIT', 'RECALCULATE_BUNDLING', 'SYNC_RECALC',
      ],
    }
  },
  computed: {
    integration() {
      return this.$store.state.integration
    },
    ranges() {
      return {
        [this.$t('productAvailability.ranges.today')]: [this.$moment(), this.$moment()],
        [this.$t('productAvailability.ranges.this_month')]: [this.$moment().startOf('month'), this.$moment().endOf('month')],
        [this.$t('productAvailability.ranges.this_year')]: [this.$moment().startOf('year'), this.$moment().endOf('year')],
        [this.$t('productAvailability.ranges.last_7_days')]: [this.$moment().subtract(7, 'days'), this.$moment()],
        [this.$t('productAvailability.ranges.last_30_days')]: [this.$moment().subtract(1, 'month'), this.$moment()],
        [this.$t('productAvailability.ranges.last_year')]: [this.$moment().subtract(1, 'year'), this.$moment()],
      }
    },
  },
  watch: {
    'visible': {
      immediate: true,
      deep: true,
      handler: function () {
        if (this.visible) {
          this.selectedMovementType = ['ALL']
        }
      },
    },
    'selectedMovementType': {
      handler: function (newVal) {
        if (!newVal || newVal.length === 0) {
          this.selectedMovementType = ['ALL']
        }
      },
    },
    'startDate': {
      deep: true,
      immediate: true,
      handler: function () {
        if (this.selectedMovementType?.length > 0 && this.startDate && this.endDate) {
          this.onCalculateRowData()
        }
      },
    },
    'endDate': {
      deep: true,
      immediate: true,
      handler: function () {
        if (this.selectedMovementType?.length > 0 && this.startDate && this.endDate) {
          this.onCalculateRowData()
        }
      },
    },
  },
  methods: {
    to(url) {
      const route = typeof url === 'object' ? url : { path: url }
      route.query = {
        ...this.to.query,
        ...this.getQueryParam({ ...this.$route.query }),
        ...url.params?.query,
      }
      
      return route
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().includes(input.toLowerCase())
      )
    },
    onSelectStateMovement(val) {
      if (val === 'ALL') {
        this.selectedMovementType = ['ALL']
      } else {
        // If ALL was selected and a specific item is selected, remove ALL and switch to that item
        if (this.selectedMovementType.includes('ALL')) {
          this.selectedMovementType = [val]
        } else {
          // Smart Select All: if all visible items are selected manually, collapse to 'ALL'
          const currentSelected = this.selectedMovementType.filter(t => t !== 'ALL')
          const isAllVisibleSelected = this.VISIBLE_MOVEMENT_TYPES.every(t => currentSelected.includes(t))
          
          if (isAllVisibleSelected) {
            this.selectedMovementType = ['ALL']
          }
        }
      }
    },
    onDeselectStateMovement(val) {
      if (val === 'ALL') {
        this.selectedMovementType = []
      } else if (this.selectedMovementType.includes('ALL')) {
        // If ALL was active but one item is deselected, expand to remaining visible items
        this.selectedMovementType = this.VISIBLE_MOVEMENT_TYPES.filter(t => t !== val)
      }
    },
    disabledDate(current) {
      return current && current > this.$moment().endOf('day')
    },
    onChangeDate(date) {
      this.startDate = date[0]
        ? this.$moment(date[0]).format(this.formatDate)
        : ''
      this.endDate = date[1]
        ? this.$moment(date[1]).format(this.formatDate)
        : ''
    },
    onSubmitModal() {
      if (this.totalRowData === 0) {
        this.$notification.error({
          message: this.$t('productAvailability.no_data_filter'),
        })
      } else {
        let movementType = [...this.selectedMovementType]
        if (movementType.includes('ALL')) {
          movementType = [...this.VISIBLE_MOVEMENT_TYPES, ...this.HIDDEN_MOVEMENT_TYPES].join(',')
        } else {
          movementType = movementType.join(',')
        }
        const selectedData = {
          startDate: this.startDate,
          endDate: this.endDate,
          selectedMovementType: movementType,
          totalData: this.totalRowData,
        }
        this.$emit('handleSubmitModal', selectedData)
        this.resetData()
      }
    },
    resetData() {
      this.startDate = ''
      this.endDate = ''
      this.selectedMovementType = []
      this.totalRowData = 0
    },
    onCalculateRowData: debounce(async function () {
      this.isCalculatingData = true
      let movementType = [...this.selectedMovementType]
      if (movementType.includes('ALL')) {
        movementType = [...this.VISIBLE_MOVEMENT_TYPES, ...this.HIDDEN_MOVEMENT_TYPES].join(',')
      } else {
        movementType = movementType.join(',')
      }
      const payload = {
        warehouse_id: this.$route.query.warehouse_id,
        catalog_id: this.catalogId,
        page: 1,
        size: 1,
        periode_awal: this.$moment(this.startDate).startOf('day').format(),
        periode_akhir: this.$moment(this.endDate).endOf('day').format(),
        movement_type: movementType,
      }
      const { data } = await getDetailStockMovementBySKU(payload)
      this.totalRowData = data.total_row || 0
      this.isCalculatingData = false
    }, 300 ),
    onBlurMovementType() {
      if (this.selectedMovementType?.length > 0) {
        this.onCalculateRowData()
      }
    },
  },
}
</script>

<style lang="scss" module>
// @import './style.module.scss';
</style>